import { ProjectModel, ProjectModelVersion } from '@/types/project-models'

export const OCR_MODEL_TYPE_ID = '234e0789-4b35-4fa0-8140-e0d0f682e9c2'
export const NER_MODEL_TYPE_ID = '60b499f1-49ef-4986-92c4-4095797c0492'
export const CATEGORY_MODEL_TYPE_ID = '4263c7ff-d64e-46a1-9e8a-28ece0e8ea1e'

export const validTrainingStatuses = [
  'training',
  'starting',
  'starting_gpu',
  'evaluating',
  'terminating_gpu',
]

export function getCurrentVersion(versions: ProjectModelVersion[] = []) {
  return versions.find((version) => version.is_current)
}

export function getVersionInCancelingState(
  versions: ProjectModelVersion[] = [],
) {
  return versions.find((version) => version.training_status === 'canceling')
}

export function getTrainingVersion(versions: ProjectModelVersion[] = []) {
  return versions.find(({ training_status }) =>
    validTrainingStatuses.includes(training_status),
  )
}

export function getNewFieldOccurrencesCount(
  trainingBatchTotal: number,
  currentModelVersion: ProjectModelVersion,
) {
  if (trainingBatchTotal === 0) return 0

  const totalTrained = currentModelVersion.field_stats?.total_trained_count || 0

  return Math.max(trainingBatchTotal - totalTrained, 0)
}

export function getNewItemOccurrencesCount(
  trainingBatchTotal: number,
  currentModelVersion: ProjectModelVersion,
) {
  if (trainingBatchTotal === 0) return 0

  const totalTrained =
    currentModelVersion.category_stats?.total_trained_count || 0

  return Math.max(trainingBatchTotal - totalTrained, 0)
}

export const getSemanticVersion = (modelVersion: ProjectModelVersion) => {
  return `v${modelVersion.version}.0`
}

export function getHighestVersion(
  model: ProjectModel,
): ProjectModelVersion | null {
  if (
    !model.project_model_versions ||
    model.project_model_versions.length === 0
  ) {
    return null
  }
  const sortedVersions = model.project_model_versions.toSorted(
    (a, b) => b.version - a.version, // Sort by version number descending
  )
  return sortedVersions[0]
}

export const getModelStatus = (model: ProjectModel) => {
  // Find if there's a version being trained
  const trainingVersion = model.project_model_versions?.find((version) =>
    [
      'starting',
      'starting_gpu',
      'training',
      'evaluating',
      'terminating_gpu',
    ].includes(version.training_status),
  )
  if (trainingVersion) return trainingVersion['training_status']
  // Find the current version using current_version_id
  const currentVersion = getCurrentVersion(model.project_model_versions)
  return currentVersion?.training_status
}

export function getStatusLabel(status: ProjectModelVersion['training_status']) {
  switch (status) {
    case 'starting':
    case 'starting_gpu':
      return 'Starting'
    case 'training':
      return 'Training'
    case 'evaluating':
      return 'Evaluating'
    case 'terminating_gpu':
      return 'Terminating GPU'
    case 'canceling':
      return 'Canceling'
    case 'canceled':
      return 'Canceled'
    case 'completed':
      return 'Trained'
    case 'error':
      return 'Error'
    default:
      return 'Not Trained'
  }
}

export const getModelVersionAccuracy = (modelVersion: ProjectModelVersion) => {
  if (modelVersion.accuracy) {
    const accuracyValue = parseFloat(modelVersion.accuracy)
    if (!isNaN(accuracyValue)) {
      return `${(accuracyValue * 100).toFixed(0)}%`
    }
  }
  return ''
}

export function getVersionIsTraining(version: ProjectModelVersion) {
  return validTrainingStatuses.includes(version.training_status)
}

export function getAccuracyColor(accuracyString: string) {
  if (!accuracyString) return undefined
  const accuracy = parseFloat(accuracyString.replace('%', ''))
  if (accuracy < 70) return 'error.main'
  if (accuracy < 90) return 'warning.main'
  return 'success.main'
}
